import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  School as KitaIcon,
  People as EmployeesIcon,
  Today as TagesplanungIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import Groups from './pages/Groups';
import Employees from './pages/Employees';
import Kita from './pages/Kita';

// Create dark theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    background: {
      default: '#121212',
      paper: '#1e1e1e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e1e1e',
        },
      },
    },
  },
});

// Navigation items
const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
  { text: 'Tagesplanung', icon: <TagesplanungIcon />, path: '/daily' },
  { text: 'Gruppen', icon: <GroupIcon />, path: '/groups' },
  { text: 'Mitarbeiter', icon: <EmployeesIcon />, path: '/employees' },
  { text: 'Kita', icon: <KitaIcon />, path: '/kita' },
];

// Placeholder components
const Dashboard = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4" gutterBottom>
      Willkommen bei KitaKey
    </Typography>
    <Typography variant="body1">
      Verwalten Sie Ihre Kita einfach und effizient
    </Typography>
  </Box>
);

const Tagesplanung = () => (
  <Box sx={{ p: 3 }}>
    <Typography variant="h4">Tagesplanung</Typography>
  </Box>
);

function Navigation() {
  const navigate = useNavigate();
  const location = useLocation();

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          boxSizing: 'border-box',
          top: ['64px', '64px', '64px'],
          height: 'auto',
          bottom: 0,
          borderRight: '1px solid rgba(255, 255, 255, 0.12)',
        },
      }}
    >
      <List>
        {menuItems.map((item) => (
          <ListItem
            button
            key={item.text}
            onClick={() => navigate(item.path)}
            selected={location.pathname === item.path}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'rgba(144, 202, 249, 0.08)',
                '&:hover': {
                  backgroundColor: 'rgba(144, 202, 249, 0.12)',
                },
              },
            }}
          >
            <ListItemIcon sx={{ color: 'inherit' }}>{item.icon}</ListItemIcon>
            <ListItemText primary={item.text} />
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <Box sx={{ 
          display: 'flex',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
        }}>
          <AppBar 
            position="fixed"
            elevation={0}
            sx={{ 
              borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Toolbar>
              <Typography variant="h6" noWrap sx={{ color: theme.palette.text.primary }}>
                KitaKey
              </Typography>
            </Toolbar>
          </AppBar>
          <Navigation />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              mt: 8,
              ml: '240px',
              backgroundColor: theme.palette.background.default,
            }}
          >
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/daily" element={<Tagesplanung />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/employees" element={<Employees />} />
              <Route path="/kita" element={<Kita />} />
            </Routes>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

export default App;